// BeeNexa Hive — derived registry selectors.
// Pure functions over the catalog + the user's installed route set. Kept
// dependency-free so they are trivial to reason about and unit-test.

import { APP_REGISTRY, appById } from './appRegistry';
import { CATEGORIES } from './categories';

// Normalize whatever `userInfo.routes` is (Set | array | undefined) to an array.
const toRouteList = (userRoutes) => (userRoutes ? [...userRoutes] : []);

// Is this app installed for the user? Only live apps with a matching route id
// can be installed; coming-soon apps never are.
export const isInstalled = (appId, userRoutes) => {
  const app = appById(appId);
  if (!app || app.status !== 'live') return false;
  return toRouteList(userRoutes).includes(app.id);
};

// Apps the user has access to (their "My Apps").
export const installedApps = (userRoutes) => {
  const routes = toRouteList(userRoutes);
  return APP_REGISTRY.filter((a) => a.status === 'live' && routes.includes(a.id));
};

export const installedStandaloneApps = (userRoutes) =>
  installedApps(userRoutes).filter((a) => a.scope === 'standalone');

export const installedPersonalApps = (userRoutes) =>
  installedApps(userRoutes).filter((a) => a.scope === 'personal');

// Everything in the catalog. Coming-soon apps are hidden for now (we only
// surface what's actually built); flip SHOW_COMING_SOON to bring them back.
export const SHOW_COMING_SOON = false;
export const storeApps = () =>
  APP_REGISTRY.filter((a) => a.scope !== 'system' && (SHOW_COMING_SOON || a.status === 'live'));

// Admin-only apps (e.g. Profit Tracker) must NOT appear in the store/browse for
// a user who hasn't been granted them — they can't even see them until an admin
// turns them on. Everything else is browse-visible.
export const isBrowseVisible = (app, userRoutes) =>
  !(entitlementOf(app) === 'admin-only' && !toRouteList(userRoutes).includes(app.id));

// The browse-facing catalog for a given user (catalog minus hidden admin-only).
export const browseApps = (userRoutes) => storeApps().filter((a) => isBrowseVisible(a, userRoutes));

// Apps within a single category the user can see.
export const appsByCategory = (categoryId, userRoutes) =>
  browseApps(userRoutes)
    .filter((a) => a.category === categoryId)
    .sort((a, b) => rankStatus(a) - rankStatus(b));

// Categories that have at least one app the user can see.
export const populatedCategories = (userRoutes) =>
  CATEGORIES.filter((c) => browseApps(userRoutes).some((a) => a.category === c.id));

// The "Popular Apps" rail — curated via the popular/popularOrder flags.
export const popularApps = (userRoutes) =>
  browseApps(userRoutes)
    .filter((a) => a.popular)
    .sort((a, b) => (a.popularOrder || 99) - (b.popularOrder || 99));

// "Recommended for you" — standalone apps the user hasn't installed yet.
export const recommendedApps = (userRoutes) => {
  const routes = toRouteList(userRoutes);
  return browseApps(userRoutes)
    .filter((a) => a.scope === 'standalone' && !routes.includes(a.id))
    .sort((a, b) => rankStatus(a) - rankStatus(b));
};

// Free-text search across name, tagline and category label (browse-visible only).
export const searchApps = (query, userRoutes) => {
  const q = (query || '').trim().toLowerCase();
  if (!q) return [];
  return browseApps(userRoutes).filter((a) => {
    const hay = `${a.name} ${a.tagline} ${a.category}`.toLowerCase();
    return hay.includes(q);
  });
};

// Access state for a given app + user, used to pick the primary action.
//   'open'          — installed & live → Open
//   'coming-soon'   — not shipped yet → disabled
//   'request'       — live but not granted to this user → Request access
export const accessState = (app, userRoutes) => {
  if (!app) return 'coming-soon';
  if (app.status === 'coming-soon') return 'coming-soon';
  if (isInstalled(app.id, userRoutes)) return 'open';
  return 'request';
};

const rankStatus = (a) => (a.status === 'live' ? 0 : 1);

// How access is obtained: 'self-serve' (request → grant, later pay) or
// 'admin-only' (a master-admin must grant it, e.g. Profit Tracker).
export const entitlementOf = (app) => app?.entitlement || 'self-serve';
